import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import {
  supporters,
  supporterPath,
  isTranslated,
  type Supporter,
} from "@/data/supporters";
import { locales, ogLocale, type Locale } from "@/i18n/config";
import { tx, tl } from "@/i18n/localized";
import { pathFor } from "@/i18n/slugMap";
import { BASE_URL } from "@/i18n/head";
import { breadcrumbList, ldScript, personSchema } from "@/lib/schema";
import { cn } from "@/lib/utils";
import { profileStrings, jobsUrl } from "@/i18n/content/profile";
import { Img, SIZES } from "@/components/Img";

const titleTemplate: Record<Locale, (name: string) => string> = {
  da: (n) => `Mød ${n}, MI Support`,
  en: (n) => `Meet ${n}, MI Support`,
  de: (n) => `${n} treffen, MI Support`,
};

const descSuffix: Record<Locale, (name: string, role: string) => string> = {
  da: (n, r) => `Mød ${n}, ${r} hos MI Support.`,
  en: (n, r) => `Meet ${n}, ${r} at MI Support.`,
  de: (n, r) => `Lernen Sie ${n} kennen, ${r} bei MI Support.`,
};

/** <head> for a profile page: title/description + canonical + hreflang. */
export function profileHead(supporter: Supporter, locale: Locale) {
  const title = titleTemplate[locale](supporter.name);
  const quote = tx(supporter.quote, locale);
  // Short quotes make too-short meta descriptions; append who the person is.
  const desc =
    quote.length < 110
      ? `${quote} ${descSuffix[locale](supporter.name, tx(supporter.role, locale))}`
      : quote.slice(0, 155);
  const alternates = locales
    .filter((l) => isTranslated(supporter, l))
    .map((l) => ({ rel: "alternate", hrefLang: l, href: BASE_URL + supporterPath(supporter, l) }));
  return {
    meta: [
      { title },
      { name: "description", content: desc },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:image", content: BASE_URL + supporter.img },
      { property: "og:type", content: "profile" },
      { property: "og:url", content: BASE_URL + supporterPath(supporter, locale) },
      { property: "og:locale", content: ogLocale[locale] },
    ],
    links: [
      { rel: "canonical", href: BASE_URL + supporterPath(supporter, locale) },
      ...alternates,
      { rel: "alternate", hrefLang: "x-default", href: BASE_URL + supporterPath(supporter, "da") },
    ],
    scripts: [
      ldScript(
        personSchema({
          name: supporter.name,
          jobTitle: tx(supporter.role, locale),
          image: BASE_URL + supporter.img,
          url: BASE_URL + supporterPath(supporter, locale),
          description: desc,
        }),
      ),
      ldScript(
        breadcrumbList([
          { name: "MI Support", url: BASE_URL + pathFor("home", locale) },
          { name: profileStrings[locale].eyebrow, url: BASE_URL + pathFor("modenmisupporter", locale) },
          { name: supporter.name, url: BASE_URL + supporterPath(supporter, locale) },
        ]),
      ),
    ],
  };
}

export function ProfileNotFound({ locale }: { locale: Locale }) {
  const t = profileStrings[locale];
  return (
    <SiteLayout>
      <section className="py-32 text-center">
        <h1 className="text-4xl font-bold">{t.notFoundHeading}</h1>
        <Link to={pathFor("modenmisupporter", locale)} className="mt-6 inline-block text-primary underline">
          {t.backToOverview}
        </Link>
      </section>
    </SiteLayout>
  );
}

export function ProfileError({ locale, error }: { locale: Locale; error: unknown }) {
  const t = profileStrings[locale];
  return (
    <SiteLayout>
      <section className="py-32 text-center">
        <h1 className="text-3xl font-bold">{t.errorHeading}</h1>
        <p className="mt-4 text-foreground/70">{t.errorBody}</p>
        {import.meta.env.DEV && (
          <pre className="mx-auto mt-4 max-w-xl overflow-auto text-left text-xs text-foreground/60">
            {(error as Error)?.message}
          </pre>
        )}
      </section>
    </SiteLayout>
  );
}

export function ProfilePage({
  supporter,
  locale,
}: {
  supporter: Supporter;
  locale: Locale;
}) {
  const t = profileStrings[locale];
  const role = tx(supporter.role, locale);
  const allOthers = supporters.filter((s) => s.id !== supporter.id && isTranslated(s, locale));
  // On Esther's page the joint Noreen & Thomas box is replaced by Dan Steen Larsen's portrait.
  const others =
    supporter.id === "esther"
      ? allOthers.filter((s) => s.id === "christian" || s.id === "maja" || s.id === "dan")
      : allOthers.slice(0, 3);

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <Img
          src={supporter.heroImg ?? supporter.img}
          alt={supporter.name}
          className={cn(
            "absolute inset-0 w-full h-full object-cover",
            supporter.heroObjectPosition ?? "object-[75%_40%]",
          )}
          sizes={SIZES.full}
          priority
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/70 to-background" aria-hidden />
        <div className="relative mx-auto max-w-4xl px-4 md:px-8 py-24 md:py-36 text-center">
          <Link
            to={pathFor("modenmisupporter", locale)}
            className="inline-flex items-center gap-2 text-sm text-foreground/80 hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> {t.backToAll}
          </Link>
          <p className="mt-6 text-sm uppercase tracking-widest text-primary font-semibold">{t.eyebrow}</p>
          <h1 className="mt-3 text-4xl md:text-6xl font-bold tracking-tight">{supporter.name}</h1>
          <p className="mt-4 text-lg md:text-xl text-foreground/85">{role}</p>
        </div>
      </section>

      {/* Quote + image */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8 grid gap-10 md:grid-cols-2 items-center">
          <div
            className={cn(
              "relative overflow-hidden rounded-2xl shadow-lg w-full",
              supporter.imgAspectClass ?? "aspect-[4/3]",
            )}
          >
            <Img
              src={supporter.quoteImg ?? supporter.img}
              alt={supporter.name}
              className={cn(
                "absolute inset-0 h-full w-full object-cover",
                supporter.imgObjectPosition ?? "object-[65%_top]",
                supporter.imgScaleClass,
              )}
              sizes={SIZES.split}
            />
          </div>
          <blockquote className="border-l-4 border-primary pl-6">
            <p className="text-xl md:text-2xl italic leading-relaxed text-foreground/90">
              "{tx(supporter.quote, locale)}"
            </p>
            <footer className="mt-4 text-sm text-foreground/70">
              — {supporter.quoteAttribution ? tx(supporter.quoteAttribution, locale) : `${supporter.name}, ${role}`}
            </footer>
          </blockquote>
        </div>
      </section>

      {/* Intro */}
      <section className="pb-12">
        <div className="mx-auto max-w-3xl px-4 md:px-8 space-y-5">
          {tl(supporter.intro, locale).map((p, i) => (
            <p key={i} className="text-lg leading-relaxed text-foreground/85">
              {p}
            </p>
          ))}
        </div>
      </section>

      {/* Sections */}
      <section className="py-12 bg-section-light text-section-light-foreground">
        <div className="mx-auto max-w-3xl px-4 md:px-8 space-y-12">
          {supporter.sections.map((sec, si) => (
            <article key={si}>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{tx(sec.heading, locale)}</h2>
              <div className="mt-4 space-y-4">
                {tl(sec.paragraphs, locale).map((p, i) => (
                  <p key={i} className="leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.ctaHeading}</h2>
          <p className="mt-6 text-foreground/85 leading-relaxed">{t.ctaBody}</p>
          <a href={jobsUrl} target="_blank" rel="noreferrer" className="mt-8 btn-cta">
            {t.ctaButton} <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Other supporters */}
      {others.length > 0 && (
        <section className="pb-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-center">{t.othersHeading}</h2>
            <div className="mt-10 grid gap-8 md:grid-cols-3">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={supporterPath(p, locale)}
                  className="group rounded-2xl bg-secondary overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
                >
                  <div className="overflow-hidden">
                    <Img
                      src={p.cardImg ?? p.img}
                      alt={p.name}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
                      sizes={SIZES.card3}
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-bold">{p.name}</p>
                    <p className="mt-1 text-sm text-foreground/70">{tx(p.role, locale)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}
